/**
 * Privisy shared API payload types.
 *
 * Single declaration site for every interface that crosses the wire between
 * the backend API and its clients. Mostly type-only: the few runtime exports
 * (`ALL_SEVERITIES`, `sectionScoreOrNull`, `isUngraded`,
 * `CROSSCHECK_DISPLAYED_VERDICTS`, `countCrosscheck`) are pure values. Backend-only
 * shapes (DB rows, scanner internals) stay in
 * packages/backend/src/models/types.ts.
 */

/** Overall compliance status for a check */
export type ComplianceStatus = 'compliant' | 'non_compliant' | 'warning' | 'error' | 'skipped';

/** Severity level for findings */
export type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info';

/**
 * Runtime list of every Severity. The Record is the point: tsc fails here the
 * next time a member joins the union, so specs that enumerate severities
 * cannot go stale. #711 added 'info' and gpc-integration.spec.ts's hand-kept
 * list drifted for five weeks (#1391).
 */
const SEVERITY_MEMBERS: Record<Severity, true> = {
  critical: true,
  high: true,
  medium: true,
  low: true,
  info: true,
};
export const ALL_SEVERITIES: readonly Severity[] = Object.freeze(
  Object.keys(SEVERITY_MEMBERS) as Severity[],
);

/** Current state of a scan */
export type ScanStatus = 'pending' | 'running' | 'completed' | 'failed';

/**
 * Where a scan stands in the queue, carried on a `'queued'` progress frame.
 * `position` is the number of scans ahead of it (0 = next to start).
 * `estimatedWaitSeconds` is a rough figure, not a promise: the queue's waves
 * ahead of this scan times a typical scan's length (api/scan-progress.ts's
 * queueStatus()).
 */
export interface ScanQueueStatus {
  position: number;
  estimatedWaitSeconds: number;
}

/**
 * Every stage name that can appear on the wire in a scan progress frame
 * (`GET /api/scans/stream`, `GET /api/scan-status/:id`, and the SSE
 * `progress` event `scan-events.ts` republishes across instances).
 * Compile-checked so a typo can't silently produce an event the frontend
 * or www never matches — see issue #779.
 *
 * Most of these are emitted by the scanner orchestrator's `emitProgress()`
 * (packages/backend/src/scanner/scanner.ts, which imports this type rather
 * than declaring its own copy). Two are emitted elsewhere on the same wire:
 * `'connected'` is the SSE handshake frame (api/routes/scans.ts), and `'queued'`
 * is the queue-position frame sent before a scan starts running
 * (api/scan-progress.ts's queuedProgress()).
 */
export type ProgressStage =
  | 'connected'
  | 'queued'
  | 'initializing'
  | 'loading'
  | 'loaded'
  | 'exploring'
  | 'exploration-complete'
  | 'ui-check'
  | 'ui-complete'
  | 'spi-detection'
  | 'spi-complete'
  | 'form-pages'
  | 'trackers'
  | 'trackers-complete'
  | 'gpc'
  | 'gpc-complete'
  | 'policy'
  | 'policy-complete'
  | 'consent-sim'
  | 'consent-sim-complete'
  | 'network-trace'
  | 'network-trace-complete'
  | 'payload-analysis'
  | 'finalizing'
  | 'crawling'
  | 'credits_exhausted'
  | 'complete'
  | 'error';

/** Insight level for human judgment items */
export type InsightLevel = 'warning' | 'info' | 'suggestion';

/** Tracker category per CCPA classification */
export type TrackerCategory =
  | 'strictly_necessary'
  | 'functional'
  | 'analytics'
  | 'marketing'
  | 'social_media'
  | 'unknown';

/**
 * Whether a vendor is positioned to receive the *contents* of a visitor's
 * communications with the site, which is the fact a California Penal Code
 * § 631 claim turns on (issue #1392). See packages/backend/src/scanner/trackers.ts
 * for the vendor list this classifies. It says what a vendor *can* capture, not
 * that it did, and not that any capture was unlawful.
 */
export type CommunicationContentCapture = 'session_replay' | 'chat';

/**
 * Which scanner stage a compliance check belongs to.
 *
 * `cipa` is deliberately outside the CCPA four. It holds prior-consent exposure
 * signals under a different statute (Penal Code § 631), and every CCPA
 * aggregator (backend helpers.ts getActiveChecks, report.ts sectionKeys, the
 * frontend's ScanResultsPage) lists the first four by name so that a CIPA
 * finding cannot move a CCPA score, section status, or gap count. Adding 'cipa'
 * to any of those lists would silently break that (issue #1392).
 */
export type SectionName = 'uiCompliance' | 'trackerDetection' | 'gpcValidation' | 'privacyPolicy' | 'cipa';

/**
 * The four CCPA stages, the sections every CCPA score, status, and gap count is
 * built from. Type the literal four-key lists with this, not SectionName, so a
 * CIPA section can never be added to one by accident (issue #1392).
 */
export type CcpaSectionName = Exclude<SectionName, 'cipa'>;

/** Why the scanner loaded a page */
export type PageRole =
  | 'homepage'
  | 'crawled'
  | 'policy'
  | 'related-policy'
  | 'dnss'
  | 'policy-pdf'
  | 'gpc-revisit'
  | 'consent-sim'
  /** A sign-in or register page the homepage links to (#1856) */
  | 'form-page';

/** How loading a page turned out */
export type PageOutcome =
  | 'read'
  | 'thin'
  /** Navigation never produced a response — DNS, TCP, TLS or timeout */
  | 'unreachable'
  /** The server answered, with a non-2xx status. `httpStatus` carries which */
  | 'http-error'
  | 'unreadable-pdf'
  | 'blocked';

/**
 * The anti-bot vendor fronting a page, classified from the main document's
 * response headers and cookies (#1339). `unknown` means a 403/429 or a
 * challenge screen that no vendor signature explained.
 */
export type BotVendor =
  | 'cloudflare'
  | 'akamai'
  | 'datadome'
  | 'perimeterx'
  | 'imperva'
  | 'siteground'
  | 'aws-waf'
  | 'unknown';

/**
 * One viewport capture of a visited page, taken the instant the stage was
 * handed the DOM (epic #1331). Metadata only: the JPEG lives in the
 * `report_screenshots` table and is served by
 * `GET /api/reports/:id/screenshots/:key`.
 */
export interface ScreenshotRef {
  role: PageRole;
  /**
   * Stable within the report: `${ledgerIndex}-${role}`, where ledgerIndex is
   * this page's position in `PageLedger.visited`.
   */
  key: string;
  width: number;
  height: number;
  /** Size of the stored JPEG in bytes */
  bytes: number;
}

/** A page the scanner actually loaded */
export interface VisitedPage {
  url: string;
  /** Why it was loaded; a page reached by two stages carries both roles */
  roles: PageRole[];
  /** Which scanner stages consumed it */
  stages: SectionName[];
  outcome: PageOutcome;
  httpStatus?: number;
  /**
   * The bot-protection vendor in front of this page. Presence only, not a
   * verdict: `outcome` still says whether the scanner got in.
   *
   * Not an independent per-page observation. When this page's own response
   * headers name no vendor, it comes from the browser context's cookies,
   * which one earlier page of the same site may have set for all of them.
   * Aggregate by origin, not by ledger row.
   */
  botVendor?: BotVendor;
  /** Characters of text extracted; present when the outcome is text-derived */
  textChars?: number;
  /** Proof-of-scan captures for this page, one per role that loaded it */
  screenshots?: ScreenshotRef[];
}

/** Why a privacy-relevant candidate page was not loaded */
export type SkipReason =
  | 'off-site'
  | 'excluded-pattern'
  | 'crawl-budget'
  | 'seed-budget'
  | 'unscannable'
  | 'near-miss';

export interface SkippedPage {
  url: string;
  reason: SkipReason;
}

/**
 * What the scan looked at. Optional on ScanResult: reports created before
 * this shipped have no ledger, and the report UI renders nothing for them.
 */
export interface PageLedger {
  visited: VisitedPage[];
  skipped: SkippedPage[];
  /** True when either cap dropped entries */
  truncated?: boolean;
}

/** Cookie report category — the five consumer-facing buckets of the cookie report */
export type CookieReportCategory =
  | 'strictly_necessary'
  | 'marketing'
  | 'analytics'
  | 'preferences'
  | 'uncategorized';

/** A single cookie observed during the scan. Cookie values are never captured. */
export interface CookieRecord {
  name: string;
  domain: string;
  path: string;
  category: CookieReportCategory;
  /** Owning vendor when the cookie matched a known definition */
  vendor?: string;
  /** Human-readable purpose when the cookie matched a known definition */
  description?: string;
  /** Unix timestamp (seconds); -1 for session cookies */
  expires: number;
  secure: boolean;
  httpOnly: boolean;
  sameSite: string;
  /** True when the cookie's domain matches the scanned site */
  firstParty: boolean;
}

/** Per-category cookie counts */
export interface CookieReportSummary {
  total: number;
  strictlyNecessary: number;
  marketing: number;
  analytics: number;
  preferences: number;
  uncategorized: number;
}

/** Complete categorized inventory of cookies observed during the scan */
export interface CookieReport {
  cookies: CookieRecord[];
  summary: CookieReportSummary;
  /**
   * Which browser session the cookies were read from (#1409): the main scan
   * session, which never clicks the consent banner's accept or reject
   * button. Absent on reports stored before this field existed.
   */
  capturedContext?: 'main_session';
  /** Reader-facing explanation of `capturedContext`, and of why these counts can differ from the tracker section's. */
  note?: string;
}

/** Raw network request data captured during scan */
export interface NetworkRequest {
  url: string;
  method: string;
  resourceType: string;
  requestHeaders?: Record<string, string>;
  responseHeaders?: Record<string, string>;
  statusCode?: number;
  requestTime: string; // ISO timestamp
  responseTime?: string; // ISO timestamp
  duration?: number; // in milliseconds
  /**
   * Encoded (on-the-wire) response body size in bytes; approximate. Taken from
   * `content-length` when the response states one, otherwise from the network
   * stack's own accounting, which subtracts an estimated header size (#1360).
   *
   * Absent means unknown — a redirect hop, a request that failed mid-body, or
   * a size the scan could not obtain in time. A recorded `0` therefore only
   * ever comes from a server-stated `content-length: 0`, never from the
   * network stack, whose own `0` is indistinguishable from unknown.
   */
  size?: number;
  initiator?: string; // what initiated this request
  isThirdParty: boolean;
  domain: string;
  failed?: boolean;
  failureReason?: string;
  /**
   * True when the browser reached this URL by automatically following a 3xx
   * redirect from a prior request, rather than the page/script issuing it
   * directly. Query parameters here may have been appended by the previous
   * hop's own `Location:` response header (the vendor's redirect target),
   * not transmitted by the site — payload inspection excludes these from
   * PII attribution (#820).
   */
  isRedirectTarget?: boolean;
  /**
   * Raw POST body, captured via Playwright's `request.postData()`, bounded
   * to a few KB. Undefined for GET/bodyless requests. Needed so payload
   * inspection can find PII sent in a request body rather than the URL —
   * Meta Advanced Matching and TikTok's hashed-email pixel calls are both
   * POST-only (#1033).
   */
  postData?: string;
}

export interface EnforcementReference {
  case: string;      // e.g. "Sephora (2022)"
  year: number;
  penalty?: string;  // e.g. "$1.2M"
  relevance: string; // one-sentence explanation of why this case applies
  url?: string;      // AG press release URL
}

/** What kind of proof an EvidenceItem carries (schema v3) */
export type EvidenceType =
  | 'request'         // a captured network request (PII transmission, tracker hit)
  | 'cookie'          // a cookie observed during the scan
  | 'tracker'         // a classified third-party tracker domain
  | 'page'            // a page URL where the issue was observed
  | 'element'         // a DOM element / link text that was found (or searched for and missing)
  | 'policy-excerpt'  // quoted snippet from the privacy policy
  | 'header'          // HTTP header or .well-known file content
  | 'api-state';      // CMP API state: USP string, GPP string, consent-mode state

/** Proof appendix section an EvidenceItem can deep-link into */
export type AppendixSection = 'network' | 'trackers' | 'cookies' | 'policy' | 'video' | 'pages';

/**
 * Typed proof attached to a compliance check (schema v3).
 * Named evidenceItems on ComplianceCheck because a legacy `evidence?: string`
 * field (policy-excerpt snippet) already exists.
 */
export interface EvidenceItem {
  type: EvidenceType;
  /** Short chip label, e.g. "doubleclick.net", "/.well-known/gpc.json", "email to facebook.com" */
  label: string;
  /** Raw matched value: full URL, cookie name, excerpt text (PII pre-redacted by stages) */
  value?: string;
  /** Deep link into the proof appendix; omitted for self-contained evidence */
  appendixRef?: { section: AppendixSection; filter?: string };
  /** Type-specific extras, e.g. { piiTypes: 'email', method: 'POST', category: 'marketing' } */
  meta?: Record<string, string | number | boolean>;
}

/** How to fix a failing check (schema v3) */
export interface CheckFix {
  /** One imperative plain-English sentence */
  summary: string;
  /**
   * Ordered concrete steps; static template from catalog, dynamic details injected by stages.
   * Readonly because CHECK_CATALOG (packages/backend/src/scanner/check-catalog.ts) is a const
   * literal — every fix template comes from there and is never mutated in place, only spread
   * into a fresh array by callers that want to append a dynamic step.
   */
  steps?: readonly string[];
  /**
   * Finishes the sentence "If it …" on an issue card where AI Crosscheck
   * disputes the pattern check (#1659), e.g. "tells consumers they can ask
   * you to delete their personal information". Lower-case, no period. Set
   * only on the privacy-policy disclosures the judge reads; the card falls
   * back to generic copy when it is absent.
   */
  coverage?: string;
}

/** How far the policy judge was allowed to affect a scan (spec: docs/superpowers/specs/2026-09-04-policy-judge-design.md). */
export type AdjudicationMode = 'shadow' | 'advisory' | 'enforce';

/**
 * The judge's verdict relative to the regex verdict. Only `upgraded` and
 * `downgraded` can move a status, and only in enforce mode.
 */
export type AdjudicationVerdict =
  | 'confirmed'   // judge agrees with the regex verdict
  | 'upgraded'    // regex FAIL; judge produced a verified verbatim quote satisfying the element
  | 'downgraded'  // regex PASS; judge rejected the excerpt and found no verified quote
  | 'unverified'  // judge claimed a quote that is not in the policy text; regex verdict kept
  | 'skipped';    // judge did not evaluate this check (error, timeout, budget)

/** Policy-judge result attached to a privacy-policy disclosure check. */
export interface CheckAdjudication {
  mode: AdjudicationMode;
  verdict: AdjudicationVerdict;
  /** true only when mode is 'enforce' and the verdict changed `status`. */
  applied: boolean;
  /** Verified verbatim quote from the policy (20–400 chars). */
  quote?: string;
  /** Document the quote was verified against. */
  quoteSourceUrl?: string;
  /** One sentence from the judge (≤ 300 chars). */
  reasoning?: string;
  model: string;
  promptVersion: string;
}

/** A single compliance check result - only for binary/testable criteria */
export interface ComplianceCheck {
  id: string;
  name: string;
  description: string;
  status: ComplianceStatus;
  severity: Severity;
  details: string;
  recommendation?: string;
  isBinaryCheck: boolean; // true if this check contributes to the score
  pointValue?: number; // Points awarded when compliant (for binary checks only)
  ccpaReference?: string; // CCPA section reference
  enforcementReferences?: EnforcementReference[]; // Enforcement actions that clarify this check
  section: SectionName; // Which scanner stage produced this check
  /**
   * True when this check's `warning` status does not itself evidence a compliance gap, for
   * one of three reasons: the underlying duty is not externally verifiable and its absence
   * is reported as information rather than a violation; the same defect is already scored
   * elsewhere as a binary failure and counting this warning too would double-count one
   * fact; or the duty was satisfied through a legally valid alternate route and the warning
   * is the disclosure of that route, not a gap (e.g. the ADMT opt-out/human-review
   * § 7221(b)(1) waiver, admt-checks.ts — issue #1237 review). Set from the check catalog
   * (`CheckCatalogEntry.advisory`), not inferred from `severity`: some `info`-severity
   * checks' warnings DO evidence an uncounted requirement (#1174 review of #1132).
   * Consumers deriving a "compliance gap" count (verdict.ts's countGapWarnings,
   * report.ts's buildSalesReportSummary) filter on this flag; severityCapFor
   * (backend helpers.ts) excludes it from the unscored-warning soft score cap for the
   * same reason. Omitted (not `false`) when the catalog declares none.
   */
  advisory?: boolean;
  /**
   * True when a `warning` from this check means the scan could not observe whether the duty
   * applies to the business (e.g. a financial-incentive notice binds only a business that
   * offers one), so the fix list groups it under "Confirm it applies to you" rather than
   * "Recommended" (issue #1657). Presentation metadata only; no score, status or cap reads
   * it. Set from the check catalog (`CheckCatalogEntry.confirmApplies`) and static per check
   * name, so it rides every status: gate on `status === 'warning'` first. Omitted (not
   * `false`) when the catalog declares none, including on reports stored before #1657.
   */
  confirmApplies?: boolean;
  /**
   * Confidence in the finding (0–1).
   * 1.0 = high confidence (either confidently absent or many patterns matched).
   * Values below 1.0 indicate partial pattern coverage on a passing check.
   * Always 1.0 for non-compliant/missing findings.
   */
  confidence?: number;
  /**
   * Short extracted evidence snippet (≤200 chars) from the scanned text.
   * Present only for passing checks where a pattern match was found.
   * Undefined when the check failed (nothing to cite) or when no text was available.
   */
  evidence?: string;
  /**
   * When policy text extraction was degraded, the quality level is stamped
   * on individual checks so consumers know the check reliability may be lower.
   * Omitted when extraction quality is 'full'.
   */
  extractionQuality?: ExtractionQuality;
  /**
   * v3: one plain-English sentence about what was found on THIS site.
   * Dynamic, emitted by scanner stages. Never contains statute citations
   * or vendor enumerations (those live in ccpaReference / evidenceItems).
   */
  finding?: string;
  /** v3: 1-2 static plain-English sentences on why the requirement matters (from check-catalog) */
  whyItMatters?: string;
  /** v3: how to fix; catalog template, stages may inject dynamic steps */
  fix?: CheckFix;
  /** v3: typed proof items (see EvidenceItem) */
  evidenceItems?: EvidenceItem[];
  /**
   * Pages this finding was derived from. Policy checks name the specific
   * document their evidence was found in; other checks name the pages their
   * stage consumed. Capped at 5 — the full inventory is in `pageLedger`.
   */
  sourceUrls?: string[];
  /**
   * Policy-judge adjudication (privacyPolicy disclosure rows only). Present in
   * advisory and enforce modes when the judge evaluated the check. Masked on
   * locked reports because the quote is proof.
   */
  adjudication?: CheckAdjudication;
}

/** A compliance insight - for items requiring human judgment (not scored) */
export interface ComplianceInsight {
  id: string;
  category: 'readability' | 'dark_patterns' | 'disclosure_quality' | 'user_experience' | 'legal_review';
  level: InsightLevel;
  message: string;
  details: string;
  recommendation?: string;
  ccpaReference?: string; // CCPA section reference
  section?: SectionName; // Which section this insight relates to
}

/** A detected third-party tracker */
export interface TrackerInfo {
  domain: string;
  url: string;
  category: TrackerCategory;
  name?: string;
  description?: string;
  dataCollected?: string[];
  isCompliant: boolean;
  /**
   * Whether this tracker was blocked by GPC. Written solely by the GPC-comparison
   * pass (`applyGPCBlockStatus`) and only for marketing/social_media categories.
   * - true: seen in control but NOT in GPC scan (blocked)
   * - false: seen in GPC scan (not blocked)
   * - undefined: seen in neither, or not applicable (inconclusive)
   */
  blockedByGPC?: boolean;
  /**
   * True when this request was a consent-denial notification — an opt-out ping to
   * an ad vendor (e.g. IAB `us_privacy=1YNN`, `deniedConsentCookieGroups`), not
   * active tracking. Excluded from the marketing-tracker finding, like `cmpManaged`
   * and `restrictedProcessing`. Distinct from `blockedByGPC`, which the two used to
   * share (#672).
   */
  consentDenialNotification?: boolean;
  /** How the tracker was loaded (script, xhr, fetch, image, etc.) */
  resourceType?: string;
  /** Domain of the frame that issued this request — the scanned page's own domain for main-frame requests, not a resolved initiator chain */
  initiator?: string;
  /** Whether this was loaded in the main frame or an iframe */
  loadContext?: 'main' | 'iframe';
  /** True when the request was issued from inside a frame served by a known CMP */
  cmpManaged?: boolean;
  /** True when the request carried a restricted-processing signal (Meta LDU / Google Consent Mode ad_storage denied / IAB US Privacy opt-out) — not a sale/share on that hit */
  restrictedProcessing?: boolean;
  /** Human-readable summary of the restriction signal(s) found, for report evidence */
  restrictionSignal?: string;
  /**
   * Set when this vendor is positioned to receive the contents of a visitor's
   * communications (session replay or a chat widget, issue #1392). Read by the
   * CIPA § 631 exposure checks. Omitted (not `false`) for every other vendor, so
   * reports that predate the flag and reports of unflagged vendors look identical.
   */
  capturesCommunicationContent?: CommunicationContentCapture;
  /**
   * The company that runs both this tracker's domain and the scanned site
   * (e.g. `'Google'` for YouTube on gmail.com, issue #1649). Such a row stays in
   * the inventory but is not a third party (Civ. Code § 1798.140(d)(2), (ai)),
   * so it is never sale/share, GPC or PII-transmission evidence. Omitted (not
   * `undefined`) on every other tracker.
   */
  sameCompanyOwner?: string;
}

/** Result of fetching and validating `/.well-known/gpc.json` */
export interface WellKnownGPCResult {
  checked: boolean;
  url: string;
  exists: boolean;
  validJson: boolean;
  declaresGPC: boolean;
  /** Alias for `exists` — whether the .well-known/gpc.json file was found */
  found: boolean;
  /** Alias for `declaresGPC` — whether the file contains `{ "gpc": true }` */
  valid: boolean;
  statusCode?: number;
  error?: string;
  /**
   * True when the fetch was answered by a bot-challenge interstitial (e.g.
   * SiteGround's sgcaptcha, HTTP 202) instead of the site, so nothing can
   * be said about whether a declaration is published.
   */
  blockedByChallenge?: boolean;
}

/** Comparison of `__uspapi` responses between control and GPC sessions */
export interface USPAPIComparisonResult {
  controlAvailable: boolean;
  gpcAvailable: boolean;
  controlValue?: string;
  gpcValue?: string;
  controlUspString?: string;
  gpcUspString?: string;
  changed: boolean;
  gpcOptedOut?: boolean;
  error?: string;
}

/** Comparison of `__gpp` responses between control and GPC sessions */
export interface GPPAPIComparisonResult {
  controlAvailable: boolean;
  gpcAvailable: boolean;
  controlValue?: string;
  gpcValue?: string;
  controlGppString?: string;
  gpcGppString?: string;
  changed: boolean;
  error?: string;
}

/** What clicking a consent banner's "Accept" unlocked on one GPC-stage page load (#1404). */
export interface ConsentAcceptObservation {
  /** Whether an Accept control was found and clicked. False means nothing was measured. */
  clicked: boolean;
  /** Third-party marketing/social domains first requested after the click. */
  marketingDomains: string[];
  /** Third-party marketing cookies first set after the click. */
  marketingCookies: string[];
  /**
   * The Accept was reached by opening a settings-only bar's own settings panel
   * rather than clicked on a top-level banner (#1451). Evidence only: what it
   * unlocks can make the GPC verdict inconclusive, but never non_compliant or
   * compliant.
   */
  viaSettingsPanel?: boolean;
}

/** See `GPCResult.marketingRequestSamples`. */
export interface GpcMarketingRequestSample {
  url: string;
  /** Playwright's `Request.resourceType()`, e.g. `script`, `image`, `xhr`. */
  resourceType: string;
}

/** See `GPCResult.statusDisplayPolarity`. */
export type GpcStatusDisplayPolarity = 'honored' | 'set-aside' | 'none';

/** GPC (Global Privacy Control) validation result */
export interface GPCResult {
  signalSent: boolean;
  signalHonored: boolean;
  /**
   * Graduated middle verdict (#836): the GPC signal was NOT fully honored
   * (some marketing tracker still fired) but the site suppressed the large
   * majority of the marketing domains it loaded without the signal AND set no
   * marketing cookies — clear evidence the signal IS being processed. Reported
   * as a scored warning naming the residual vendors, not a wholesale CRITICAL
   * failure. Only meaningful when `signalHonored` is false and `isInconclusive`
   * is falsy; the two verdicts are mutually exclusive.
   */
  signalSubstantiallyHonored?: boolean;
  marketingCookiesDisabled: boolean;
  thirdPartyRequestsBlocked: boolean;
  hasStatusDisplay: boolean;
  /**
   * Which kind of status display the GPC session showed (#1329). `honored`
   * mirrors `hasStatusDisplay`; `set-aside` is a visible message that this
   * visitor's signal was not applied, with a reason or a settings link — the
   * display § 7025(c)(3)/(c)(4) require when a business lawfully sets the
   * signal aside. Absent on reports produced before #1329.
   */
  statusDisplayPolarity?: GpcStatusDisplayPolarity;
  /**
   * #1547: `hasStatusDisplay` rests only on detection-only wording ("GPC
   * enabled", "GPC detected"), never on a phrase saying the opt-out was
   * processed. Changes the check's copy, not its status. Absent when false
   * and on reports produced before #1547.
   */
  statusDisplayDetectionOnly?: boolean;
  details: string;
  /**
   * Consent/cookie prompt shown while the GPC signal was active. Permitted by
   * § 7025(c)(3) (business must still process the signal), but displaying it
   * forfeits frictionless processing under § 7025(f)(3).
   */
  consentOverrideDetected?: boolean;
  /** Evidence snippet for consent override finding */
  consentOverrideEvidence?: string;
  /** Immediate re-opt-in prompt shown in same GPC session (§ 1798.135(c)(4)) */
  immediateReOptInDetected?: boolean;
  /** Evidence snippet for immediate re-opt-in finding */
  immediateReOptInEvidence?: string;
  /**
   * The detected re-opt-in prompt is a financial-incentive-program withdrawal
   * affirmation (loyalty/rewards/discount/coupon), which § 7025(c)(4)
   * expressly permits — not a § 1798.135(c)(4) violation.
   */
  immediateReOptInFinancialIncentive?: boolean;
  /**
   * The GPC pass's consent-dialog probe never read the page (its execution
   * context was reset on both attempts), so the consent-override and re-opt-in
   * fields record no measurement (#1999).
   */
  conflictProbeFailed?: boolean;
  /** List of marketing/social tracker domains that were still loaded despite GPC signal */
  marketingDomainsLoaded?: string[];
  /** Names of marketing/advertising cookies still present after GPC signal */
  marketingCookiesFound?: string[];
  /**
   * #1713: the first request each `marketingDomainsLoaded` domain made under
   * `Sec-GPC: 1`, keyed by domain. `url` is origin + path only: the query
   * string and fragment are dropped because tracker requests carry IDs and
   * PII there. Absent on reports produced before #1713.
   */
  marketingRequestSamples?: Record<string, GpcMarketingRequestSample>;
  /**
   * #1713: the domain each `marketingCookiesFound` label was set on, keyed by
   * label. Absent on reports produced before #1713.
   */
  marketingCookieDomains?: Record<string, string>;
  /** Result of checking the site's `/.well-known/gpc.json` declaration */
  wellKnownGPC?: WellKnownGPCResult;
  /** Comparison of `__uspapi` output between control and GPC sessions */
  uspapi?: USPAPIComparisonResult;
  /** Comparison of `__gpp` output between control and GPC sessions */
  gpp?: GPPAPIComparisonResult;
  /**
   * Control scan result: marketing domains observed WITHOUT the GPC signal.
   * Used to determine whether the environment itself suppresses ad trackers
   * (e.g. datacenter IPs), making a clean GPC result inconclusive.
   */
  controlMarketingDomains?: string[];
  /**
   * What the GPC verdict rests on:
   *  - `observed`: the control and GPC passes produced a usable tracker delta.
   *  - `declaration`: neither pass saw trackers; honored on the strength of a
   *    valid `/.well-known/gpc.json` alone.
   *  - `overridden-by-consent` (#1404): neither pass saw trackers because the
   *    site is opt-in, and clicking the consent banner's "Accept" in a GPC
   *    context loaded marketing trackers — a generic cookie consent overrode
   *    the opt-out. Not honored.
   *    Since #1845, only when the GPC session's prompt answered the signal and
   *    the site also showed a status display claiming the opt-out.
   *  - `consent-gated` (#1404): the same Accept loaded marketing trackers
   *    without GPC but nothing with it. Honored, from observed behavior.
   *  - `unsettled-consent` (#1845): neither pass saw trackers, and a generic
   *    Accept in a GPC context loaded them. Whether that Accept is § 7025(c)(3)
   *    consent is unsettled, so the result is inconclusive (an unscored high
   *    warning) and keeps the post-Accept evidence.
   */
  signalHonoredBasis?: 'observed' | 'declaration' | 'overridden-by-consent' | 'consent-gated' | 'unsettled-consent';
  /**
   * Accept-under-GPC pass (#1404): a GPC-context page load whose consent
   * banner "Accept" was clicked. Only run when the control and GPC passes were
   * both empty and a banner was detected — i.e. on opt-in CMP sites, where
   * what Accept unlocks for a GPC visitor is the only GPC question left. On a
   * settings-only bar the Accept is reached through its settings panel and
   * `viaSettingsPanel` is set (#1451).
   * Kept apart from `marketingDomainsLoaded`, which feeds the tracker table's
   * pre-consent `blockedByGPC` status.
   */
  acceptUnderGpc?: ConsentAcceptObservation;
  /**
   * The same Accept click without the GPC signal (#1404). Only run when the
   * accept-under-GPC pass unlocked nothing, to tell a site that honors GPC
   * after consent from an environment ad vendors do not serve at all.
   */
  acceptWithoutGpc?: ConsentAcceptObservation;
  /**
   * Whether the GPC result is inconclusive because the control scan also
   * observed no marketing trackers (environment-level suppression).
   */
  isInconclusive?: boolean;
  /** Human-readable explanation when isInconclusive is true */
  inconclusiveReason?: string;
  /**
   * Number of marketing domain requests that were identified as consent-denial
   * notifications (GPC opt-out pings to ad vendors) and excluded from the
   * "marketing trackers still loaded" count. A non-zero value here is a
   * positive signal — the site is correctly notifying ad vendors of the opt-out.
   */
  consentDenialNotificationsFiltered?: number;
  /**
   * Whether GPC is processed frictionlessly per § 1798.135(b)(1).
   * When true, the business is NOT required to post a "Do Not Sell or Share"
   * link or an Alternative Opt-out Link — GPC alone satisfies the opt-out
   * requirement (Path 3).
   */
  frictionlessGPC?: boolean;
  /**
   * Whether a CMP / consent banner was detected during the control scan
   * (no GPC signal). When true alongside consentOverrideDetected/
   * immediateReOptInDetected, the banner is site-wide and not presented
   * "in response to" the GPC signal — downgrade to warning per § 7025(c)(3-4).
   */
  controlCMPBannerPresent?: boolean;
  /**
   * A consent banner was seen without the GPC signal, by the control scan's
   * CMP probe or by the UI compliance crawl (which runs without GPC). A
   * hand-rolled banner the CMP selectors miss only shows up in the latter;
   * either way the banner is shown to every visitor, so a dialog observed
   * under GPC is site-wide rather than "in response to" the signal (#1091).
   */
  bannerSeenWithoutGpc?: boolean;
}

/** Privacy policy analysis result */
export interface PrivacyPolicyAnalysis {
  found: boolean;
  url?: string;
  hasDataCategories: boolean;
  hasCollectionPurposes: boolean;
  hasRightToKnow: boolean;
  hasRightToDelete: boolean;
  hasRightToCorrect: boolean;
  hasRightToOptOut: boolean;
  /**
   * True when the opt-out right the policy describes covers *sharing*, not
   * only sale (§ 7011(e)(2)(D)). Absent on reports scanned before #1851;
   * `false` with `hasRightToOptOut` true means a sale-only right.
   */
  hasRightToOptOutShare?: boolean;
  has12MonthLookback: boolean;
  hasServiceProviderDisclosure: boolean;
  // Priority 1 (Critical)
  hasCategoriesOfSources: boolean;
  hasCategoriesOfThirdParties: boolean;
  hasADMTDisclosure: boolean;
  /**
   * True when every ADMT mention in the policy denies using the technology
   * ("we do not use automated decision-making"). The § 7220 Pre-use Notice
   * content requirements bind only a business that uses ADMT (§ 7200(a)), so
   * the seven ADMT sub-checks are not emitted in that case.
   */
  admtUsageDenied?: boolean;
  /**
   * True when the policy states that the business uses ADMT to make a
   * significant decision (§ 7200(a)): credit, housing, education, employment
   * or healthcare. Without it the ADMT sub-checks are advisory, since
   * § 7011(e)(2)(F)-(G) apply only "if the business uses ADMT as set forth in
   * section 7200, subsection (a)" (#1841). Absent is read as false.
   */
  admtSignificantDecisionUse?: boolean;
  // Legacy ADMT sub-disclosures retained for backward compatibility.
  hasADMTLogic?: boolean;
  hasADMTPurpose?: boolean;
  hasADMTDataCategories?: boolean;
  hasADMTOptOut?: boolean;
  hasADMTHumanReview?: boolean;
  hasADMTAccess?: boolean;
  hasADMTOutputType?: boolean;
  hasSensitivePIUsage: boolean;
  /** § 1798.121 / § 7011(e)(2)(E) right-to-limit disclosure detected in the policy */
  hasRightToLimit?: boolean;
  /**
   * True when every `hasSensitivePIUsage` match denies collecting/using
   * sensitive PI, or limits it to the § 7027(m)/§ 1798.121-permitted
   * purposes. When true, buildRightToLimitCheck treats the § 7014 usage
   * condition as not evidenced (issue #1023).
   */
  spiUsageLimitedOrDenied?: boolean;
  /**
   * § 7011(e)(1)(D) categories-sold/shared list detected (#1850). One of the
   * two forms that satisfy `has12MonthLookback`.
   */
  hasCategoriesSoldOrShared?: boolean;
  /**
   * The policy states it sold or shared no personal information in the
   * preceding 12 months, with a denial covering both sale and sharing
   * (#1850). The other form that satisfies `has12MonthLookback`.
   */
  hasNoneSoldAndSharedInLookback?: boolean;
  /**
   * § 7011(e)(1)(E) per category (#1850): the policy maps the categories of
   * personal information sold or shared to the categories of third parties
   * receiving them. When the scan saw sale/share evidence, Policy: Categories
   * of Third Parties needs this, not just a generic sharing sentence.
   */
  hasCategoryRecipientMapping?: boolean;
  // Legacy CPRA/CCPA detail flags retained for backward compatibility.
  hasSellingOrSharingPurpose?: boolean;
  hasOptOutPreferenceSignal?: boolean;
  /**
   * True when the policy's opt-out-preference-signal disclosure
   * affirmatively states that the business processes the signal (e.g. GPC)
   * frictionlessly — not merely that it isn't denied. `hasOptOutPreferenceSignal`
   * alone satisfies the § 7011(e)(2)(G) disclose-whether duty even on a bare
   * mention or a denial, but only this flag may satisfy the § 7025(g)(2)(B)
   * Path-3 frictionless exemption in dnss-reconciliation.ts (issue #1181,
   * tightened by #1209 to require the actual affirmative statement rather
   * than just the absence of a denial).
   */
  hasOptOutPreferenceSignalAffirmative?: boolean;
  /**
   * True when every opt-out-preference-signal mention in the policy sits
   * inside a sentence denying that the business honors the signal ("We do
   * not currently process opt-out preference signals, including Global
   * Privacy Control."). Distinct from `!hasOptOutPreferenceSignalAffirmative`,
   * which is also true for a bare, non-committal mention that neither denies
   * nor affirms — this flag lets a consumer distinguish "the policy denies
   * honoring GPC" from "the policy names GPC without committing to anything"
   * so remediation text doesn't accuse a non-committal policy of a denial it
   * never made (issue #1209 review).
   */
  hasOptOutPreferenceSignalDenied?: boolean;
  /**
   * True when a signal mention is followed, within its sentence or the next
   * two, by the § 7011(e)(3)(F) processing scope: the browser/device, and a
   * known consumer account or offline sales (issue #1849). Only an explicit
   * `false` downgrades Policy: Opt-Out Preference Signal Disclosure to a
   * warning; a report analysed before the flag existed leaves it undefined.
   */
  hasOptOutPreferenceSignalScoped?: boolean;
  /**
   * The first policy sentence that denies selling or sharing personal
   * information outright ("We do not sell or share your personal
   * information."), trimmed to 200 characters. Not dispositive: when the scan
   * observes sale/share evidence, the Do Not Sell or Share Link, Right to
   * Opt-Out and Policy Ownership findings quote it to explain which evidence
   * overrode it (issue #1651). Undefined when the policy has no such sentence.
   */
  noSaleShareStatement?: string;
  // Priority 2 (High)
  hasMinorsSaleDisclosure: boolean;
  hasNonDiscrimination: boolean;
  // Priority 3 (Medium)
  hasVerificationProcess: boolean;
  hasPrivacyContact: boolean;
  hasAuthorizedAgent: boolean;
  // Priority 4 (Low)
  hasLastUpdated: boolean;
  hasFinancialIncentive: boolean;
  hasFinancialIncentiveOffer?: boolean;
  /**
   * Which § 7016(d) notice elements the policy states (#1864). A notice is
   * complete only when all three are present. Absent on analyses persisted
   * before it existed, which fall back to `hasFinancialIncentive`.
   */
  financialIncentiveElements?: { terms: boolean; optInOrWithdraw: boolean; value: boolean };
  /**
   * The policy denies offering an incentive ("we do not offer financial
   * incentives"). Element wording beside a denial ("if we do, you may
   * withdraw") is hypothetical, so it does not make a partial notice (#1864).
   */
  hasFinancialIncentiveDenial?: boolean;
  details: string;
  // Additional pages scanned beyond main privacy policy
  additionalPagesScanned?: string[];
  // Track mentions of minors/children without specific age 16
  hasMinorsRelatedMention?: boolean;
  // An authorized-agent mention, with or without the instructions that set
  // hasAuthorizedAgent (#1854)
  hasAuthorizedAgentMention?: boolean;
  // Track if policy has an age restriction statement (e.g., "must be 18+")
  hasAgeRestriction?: boolean;
  // Form-based request mechanism analysis
  requestFormUrl?: string;
  requestFormRightsOffered?: string[];
  hasRequestForm?: boolean;
  /**
   * @internal Ephemeral — populated during scanning, NOT persisted to DB.
   * Contains the concatenated raw policy text for evidence extraction in generatePolicyChecks.
   */
  _rawText?: string;
  /**
   * @internal Ephemeral — populated during scanning, NOT persisted to DB.
   * Per-document source text (HTML pages and followed PDFs), so evidence extraction
   * in generatePolicyChecks can attribute a disclosure to the specific document it
   * was found in instead of the generic "the privacy policy" (#696).
   */
  _rawDocuments?: { url: string; text: string; hidden?: boolean }[];
  /**
   * @internal Ephemeral — populated during scanning, NOT persisted to DB.
   * True when `found` is false because the homepage itself yielded zero
   * navigable links at all (a WAF interstitial, bot-challenge page, or an
   * unhydrated client-rendered app) — not because the site genuinely
   * publishes no privacy policy. Distinguishes "we could not look" from
   * "we looked and there is nothing", the same distinction `extractionQuality`
   * draws once a policy document is in hand (#1236 review).
   */
  _policyDiscoveryDegraded?: boolean;
  /**
   * @internal Ephemeral — populated during scanning, NOT persisted to DB.
   * The home-page privacy link whose page did not read as a privacy policy
   * (`looksLikePrivacyPolicyDocument`), set whether or not a policy was then
   * found elsewhere. The site verdict reads it to reconcile Stage 2's
   * "Privacy Policy Link" check, which passed on the link's label alone.
   */
  _rejectedPolicyLink?: string;
  /**
   * @internal Ephemeral — populated during scanning, NOT persisted to DB.
   * Whether the policy body (not the site header, nav or footer) links to the
   * request form at `requestFormUrl`; undefined when it could not be observed.
   * Privacy Request Mechanism reads it for § 7011(e)(3)(B) (#1862).
   */
  _requestFormLinkedFromPolicy?: boolean;
  /**
   * @internal Ephemeral — populated during scanning, NOT persisted to DB.
   * The link text or policy phrase that shows the business serves consumers
   * at a physical location (a store finder, "visit our cafés"). Privacy
   * Request Mechanism reads it for § 1798.130(a)(1)(A)'s toll-free duty (#1862).
   */
  _physicalLocationEvidence?: string;
  /**
   * @internal Ephemeral — populated during scanning, NOT persisted to DB.
   * Whether the policy's links carry a mailbox (`mailto:`) or a US toll-free
   * number (`tel:`), which its innerText does not show. Privacy Request
   * Mechanism fails on observed absence only when this was observed and
   * found neither (#1862); undefined keeps the check a warning.
   */
  _policyContactLinks?: { email: boolean; tollFree: boolean };
  /**
   * @internal Ephemeral — populated during scanning, NOT persisted to DB.
   * Email addresses the policy offers, from its `mailto:` links and its text,
   * lowercased and deduped. The request email MX pass (#2102) reads them.
   */
  _policyRequestEmails?: string[];
  /**
   * @internal Ephemeral — populated during scanning, NOT persisted to DB.
   * Each HTML policy page cut at its headings, so the Notice at Collection
   * content reader (#2099) can read one section and the links inside it.
   */
  _rawOutlines?: { url: string; blocks: NoticeOutlineBlock[] }[];
  /** ISO-639-1 language code detected from policy text (e.g. 'en', 'es', 'fr') */
  detectedLanguage?: string;
  /** Overall quality of the text extraction for this policy */
  extractionQuality?: ExtractionQuality;
  /** Structured warnings about extraction quality or coverage gaps */
  coverageWarnings?: CoverageWarning[];
  /** True when the privacy policy URL is hosted on a different domain from the scanned site */
  isExternalPolicy?: boolean;
  /** Registrable domain of the policy URL (set when isExternalPolicy is true) */
  policyDomain?: string;
  /** Registrable domain of the scanned site (set when isExternalPolicy is true) */
  siteDomain?: string;
  /** True when the policy is hosted on a known generic platform (e.g. Google, Termsfeed) */
  isGenericPlatformPolicy?: boolean;
}

// ---------------------------------------------------------------------------
// Coverage warning types (extraction quality)
// ---------------------------------------------------------------------------

/** Quality grade for policy text extraction */
export type ExtractionQuality = 'full' | 'partial' | 'gated' | 'failed';

/** Machine-readable code for a coverage gap */
export type CoverageWarningCode =
  | 'LOW_TEXT_YIELD'
  | 'PAGE_LOAD_ERROR'
  | 'SINGLE_PAGE_ONLY'
  | 'PDF_ONLY'
  /** A privacy-relevant PDF was discovered but yielded no usable text */
  | 'PDF_UNREADABLE'
  | 'NO_TEXT_EXTRACTED'
  | 'LANGUAGE_NOT_SUPPORTED';

/** A structured warning about a coverage gap in policy text extraction */
export interface CoverageWarning {
  code: CoverageWarningCode;
  message: string;
  affectedUrl?: string;
}

// ---------------------------------------------------------------------------
// Section-based scan result structure (schema version 2)
// ---------------------------------------------------------------------------

/** Score breakdown for a single section */
export interface SectionScore {
  earnedPoints: number;
  totalPoints: number;
  percentage: number;
  passedChecks: number;
  totalChecks: number;
}

/** Overall score breakdown for transparency */
export interface ScoreBreakdown {
  totalPoints: number;
  earnedPoints: number;
  percentageScore: number;
  binaryChecksCount: number;
  passedChecksCount: number;
}

/** UI Compliance section details */
export interface UIComplianceDetails {
  doNotSellUrl?: string;
  consentBannerFound: boolean;
}

/** Tracker Detection section details */
export interface TrackerDetectionDetails {
  trackers: TrackerInfo[];
  summary: {
    totalThirdParty: number;
    marketing: number;
    analytics: number;
    socialMedia: number;
    functional: number;
    unknown: number;
  };
}

/** Network trace statistics (pre-computed for the UI) */
export interface NetworkTraceStats {
  totalRequests: number;
  thirdPartyCount: number;
  firstPartyCount: number;
  failedCount: number;
  totalSize: number;
  avgDuration: number;
  resourceTypes: Record<string, number>;
}

/** Network trace with pre-computed stats */
export interface NetworkTraceDetails {
  requests: NetworkRequest[];
  stats: NetworkTraceStats;
}

/** GPC Validation section details (alias of GPCResult) */
export type GPCValidationDetails = GPCResult;

/** Privacy Policy section details (alias of PrivacyPolicyAnalysis) */
export type PrivacyPolicyDetails = PrivacyPolicyAnalysis;

/**
 * Generic section envelope.
 * Each scanner stage produces one of these, containing its checks,
 * insights, score, and stage-specific detail data.
 */
export interface ScanSection<T> {
  status: ComplianceStatus;
  score: SectionScore;
  checks: ComplianceCheck[];
  insights: ComplianceInsight[];
  details: T;
  stageError?: string;
}

/** All scan sections, keyed by stage name */
export interface ScanSections {
  uiCompliance: ScanSection<UIComplianceDetails>;
  trackerDetection: ScanSection<TrackerDetectionDetails>;
  gpcValidation: ScanSection<GPCValidationDetails>;
  privacyPolicy: ScanSection<PrivacyPolicyDetails>;
  /**
   * CIPA § 631 exposure signals (issue #1392). Optional because reports
   * persisted before the section existed do not carry it. Never part of the
   * CCPA score: see SectionName.
   */
  cipa?: ScanSection<CipaDetails>;
}

/** Details for the CIPA exposure section: what was seen, not a verdict (issue #1392). */
export interface CipaDetails {
  /** Vendors in the pre-consent tracker inventory that can capture communication content. */
  contentCaptureVendors: Array<{
    domain: string;
    name: string;
    capture: CommunicationContentCapture;
  }>;
  /**
   * Whether a consent banner existed at all. No banner is the higher-exposure
   * case. Undefined when banner detection did not run (UI compliance disabled
   * or failed), so the report makes no claim about a banner either way.
   */
  consentBannerFound?: boolean;
}

/**
 * Whether a section's `score.percentage` is an actual measurement, or should
 * be treated as "no number to show." `buildSkippedSection` (backend
 * utils/helpers.ts) fabricates a real-shaped score of all zeroes for a
 * section that never ran, so every reader of `score.percentage` has to check
 * `status` first or it renders a run that measured nothing as a run that
 * measured total failure. Some sections also complete without measuring
 * anything despite a normal terminal `status` — e.g. an inconclusive GPC run
 * with no marketing trackers to test — which only the section-specific
 * `details` shape knows, so the caller passes that in as `isInconclusive`
 * rather than this helper trying to inspect `details` itself.
 *
 * Answers only "is there a number," not what to show instead: ReportHero's
 * three-way "N/A" / "stage failed" / "?" distinction for the null case is a
 * display decision for its own caller, not this helper's (#1168).
 */
export function sectionScoreOrNull(
  section: Pick<ScanSection<unknown>, 'status' | 'score'>,
  isInconclusive = false,
): number | null {
  if (section.status === 'skipped' || section.status === 'error' || isInconclusive) return null;
  return section.score.percentage;
}

/**
 * Whether a whole scan assessed nothing, so its persisted score of 0 must be
 * shown as N/A rather than graded F (#1128 follow-up review). The backend's
 * determineOverallStatus() (utils/helpers.ts) returns 'error' only when no
 * check is active, and failScan() stamps it on a scan that never finished —
 * this is the single definition of that convention every reader shares.
 */
export function isUngraded(overallStatus: ComplianceStatus | string | undefined): boolean {
  return overallStatus === 'error';
}

/**
 * Where a scan's browser traffic left from. Sites geo-target CCPA elements
 * (usaa.com shows its "Do Not Sell or Share" link only to California
 * visitors), so prod routes scans through a California proxy (SCAN_PROXY_URL).
 * `proxy`: through it. `direct`: no proxy configured (local, tests).
 * `proxy-unreachable`: configured but down, so the scan fell back to the
 * host's own IP and geo-targeted findings carry a caveat.
 * `retry-proxy`: the site blocked the first attempt (HTTP 403/429 or a
 * challenge page), so the scan re-ran once through the California
 * residential proxy (SCAN_BLOCK_RETRY_PROXY_URL, #1869).
 */
export type ScanEgress = 'proxy' | 'direct' | 'proxy-unreachable' | 'retry-proxy';

/** Complete scan result for a website (schema version 2) */
export interface ScanResult {
  id: string;
  url: string;
  status: ScanStatus;
  startedAt: string;
  completedAt?: string;
  overallStatus: ComplianceStatus;
  score: number; // Binary score from 0-100 based only on testable criteria
  scoreBreakdown: ScoreBreakdown; // Overall score information
  schemaVersion: number; // Always 2 for this structure
  /** ID of the Clerk user who initiated the scan (undefined for unauthenticated scans). */
  userId?: string;
  /** Intent token for durable claims of anonymous scans. */
  intentToken?: string;
  /** Whether the full forensic audit is unlocked for this scan. */
  licenseStatus?: 'locked' | 'unlocked';

  /** Persistent report ID — present when this scan has been saved as a report. */
  reportId?: string | null;

  /** Section-based results — each scanner stage is self-contained */
  sections: ScanSections;

  /** Network trace across the entire scan */
  networkTrace: NetworkTraceDetails;

  /**
   * Categorized inventory of all cookies observed on the main page.
   * Optional/additive — absent on older reports and on scans where the
   * tracker-detection section was disabled.
   */
  cookieReport?: CookieReport;

  pagesScanned: number;
  /**
   * Every page URL the scanner loaded and analysed, start URL first. Pages
   * that failed to load, answered an HTTP error or served a bot-challenge
   * screen are excluded (they appear in `pageResults` and `pageLedger`).
   * `pagesScanned` is its count (#1368).
   */
  pagesCrawled: string[];
  /**
   * What the scan looked at: pages loaded, and privacy-relevant pages
   * considered and skipped. Absent on reports created before this shipped.
   */
  pageLedger?: PageLedger;
  errors: string[];
  /** True when the target site's bot protection prevented the scanner from loading the real page. */
  blockedBySite?: boolean;
  /**
   * Where the homepage landed, set only when the start URL redirected onto a
   * different registrable domain (gmail.com → accounts.google.com). The
   * report then describes that page, and the landed domain counts as the
   * site for first/third-party classification (#1648). Absent otherwise.
   */
  homepageFinalUrl?: string;
  /** Where the scan's browser traffic left from. Absent on reports created before it shipped. */
  scanEgress?: ScanEgress;
  /**
   * AI Crosscheck state when the judge did not run at scan time (#1960). An
   * anonymous audit in advisory mode defers the judge to unlock: `pending`
   * until the deferred run writes its verdicts (the field is then removed),
   * `unavailable` when the policy documents were purged before it could run.
   * Absent whenever the judge ran (or was off) at scan time.
   */
  crosscheckStatus?: 'pending' | 'unavailable';
  videoPath?: string; // Path to recorded video file
  scannerVersion?: string; // Scanner engine version at time of scan
  scoringVersion?: string; // Scoring methodology version at time of scan
  /** Per-page results (populated when crawl mode is active, i.e. maxPages > 1) */
  pageResults?: PageScanResult[];
}

// ---------------------------------------------------------------------------
// Per-page result (populated when crawl mode is active)
// ---------------------------------------------------------------------------

/** Per-page scan result within a crawl */
export interface PageScanResult {
  url: string;
  checks: ComplianceCheck[];
  trackers: TrackerInfo[];
  gpcResult?: GPCResult;
  errors: string[];
  /** Network trace for this specific page */
  networkTrace?: NetworkRequest[];
  /**
   * Set when the page is on a different registrable domain than the scanned
   * site (a cross-site `urls` entry, or a seed that redirected off-site). Its
   * evidence is excluded from the site's sections, and its checks must not be
   * counted toward the site's totals either (#1364). Absent for on-site pages.
   */
  offSite?: boolean;
  /**
   * Set on a sign-in or register page the form pages pass visited (#1856),
   * as opposed to a crawled page. Absent otherwise.
   */
  formPage?: boolean;
  /**
   * Set on the page the opt-out link opens, visited by the opt-out link
   * destination pass (#2084): whether it offers a working sale/share
   * opt-out. Its one check is unscored and outside every section. Absent
   * otherwise.
   */
  optOutDestination?: OptOutDestinationEvidence;
  /**
   * Set on the same entry when the link opt-out pass (#2085) completed the
   * opt-out there and watched later page loads: whether ad-tech sale/sharing
   * stopped. Its check is unscored and outside every section. Absent otherwise.
   */
  linkOptOut?: LinkOptOutEvidence;
  /**
   * Set on its own entry (the homepage) when the GPC-absence pass (#2095)
   * revisited the site without GPC in the GPC pass's browser storage: whether
   * ad beacons resumed with identifiers set during the GPC visit. Its check is
   * unscored and outside every section. Absent otherwise.
   */
  gpcAbsence?: GpcAbsenceEvidence;
  /**
   * Set on its own entry (the notice page) when the Notice at Collection
   * content reader (#2099) located the notice section, or found a notice page
   * the scan never read: whether it says if each category is sold or shared
   * (§ 7012(e)(3)) and links the Notice of Right to Opt-out (§ 7012(e)(5)).
   * Its checks are unscored and outside every section. Absent otherwise.
   */
  noticeContents?: NoticeContentsEvidence;
  /**
   * Set on its own entry (the policy page) when the request email MX pass
   * (#2102) looked up the mail servers of the addresses the site offers:
   * whether each one's domain can receive mail. Its check is unscored and
   * outside every section. Absent otherwise.
   */
  requestEmailMx?: RequestEmailMxEvidence;
  /**
   * Set on its own entry (the homepage) when the incentive notice pass
   * (#2121) checked each homepage financial incentive offer for a Notice of
   * Financial Incentive where the visitor meets it (§ 7016(c)). Its checks
   * are unscored and outside every section. Absent otherwise.
   */
  incentiveOfferNotice?: IncentiveOfferNoticeEvidence;
}

/** One homepage incentive offer and whether its notice is where the offer is made (#2121). */
export interface IncentiveOfferNoticeOffer {
  kind: 'loyalty' | 'signup-discount';
  /** The offer as the visitor sees it. */
  text: string;
  /** The page or link the offer was found at. */
  url: string;
  verdict: 'pass' | 'fail' | 'unclear';
  /** One sentence for the report. */
  reason: string;
  /** The notice link that passed it, when one did. */
  noticeLink?: { href: string; text: string };
  /**
   * Whether joining is featured more prominently than declining
   * (#2126, § 7004(a)(2)(E)). Absent when the choice pass is switched off.
   */
  choice?: IncentiveOfferChoice;
}

/** A join or decline control as measured on the homepage (#2126): snapshot numbers only. */
export interface IncentiveOfferChoiceControl {
  /** Its label, or its aria-label when it has no visible text. */
  text: string;
  width: number;
  height: number;
  fontSizePx: number;
  /** A filled background that stands out from the pop-up, or a visible border. */
  filled: boolean;
  underlined: boolean;
}

/** The join/decline symmetry verdict for one incentive offer (#2126, #1870 R-68). */
export interface IncentiveOfferChoice {
  verdict: 'pass' | 'fail' | 'unclear';
  /** One sentence for the report. */
  reason: string;
  join?: IncentiveOfferChoiceControl;
  decline?: IncentiveOfferChoiceControl;
}

/** What the incentive notice pass saw (#2121, #1870 R-65, § 7016(c)). Loyalty first, at most one per kind. */
export interface IncentiveOfferNoticeEvidence {
  offers: IncentiveOfferNoticeOffer[];
}

/** One offered email address and its domain's MX lookup (#2102). */
export interface RequestEmailMxAddress {
  address: string;
  /** Lowercased domain, as looked up. */
  domain: string;
  /** `skipped`: past the per-scan domain cap, never looked up and never a fail. */
  verdict: 'pass' | 'fail' | 'unclear' | 'skipped';
  /** MX exchanges, by priority, for a domain that has them. */
  mxHosts?: string[];
  /** The resolver's error code (`ENODATA`, `ENOTFOUND`, `ETIMEOUT`, ...) or `NULL_MX` (RFC 7505). */
  errorCode?: string;
}

/** What the request email MX pass saw (#2102, #1870 R-58, § 7004(a)(5)(B)). */
export interface RequestEmailMxEvidence {
  verdict: 'pass' | 'fail' | 'unclear' | 'not_applicable';
  /** One sentence for the report. */
  reason: string;
  /** Every address found, in order of appearance. Capped. */
  addresses: RequestEmailMxAddress[];
}

/**
 * One heading-bounded block of a policy page (#2099): the heading, the text up
 * to the next heading, and the links inside that text only. Ephemeral.
 */
export interface NoticeOutlineBlock {
  heading: string;
  /** 1-6 for h1-h6 or an aria-level; null for a bold-line pseudo-heading. */
  level: number | null;
  /** The heading's id/name, an anchor's inside or just before it, and a wrapper's id it opens. */
  ids: string[];
  /** innerText-like; a table row is one line with its cells separated by tabs. */
  text: string;
  links: { text: string; href: string }[];
}

/** One § 7012(e) element of the Notice at Collection content reader (#2099). */
export interface NoticeContentsRow {
  verdict: 'pass' | 'fail' | 'unclear' | 'not_applicable';
  /** One sentence for the report. */
  reason: string;
  /** The matched sentence or table header, at most 200 characters. */
  quote?: string;
  /** The opt-out-notice link found inside the notice section. */
  linkHref?: string;
}

/** What the Notice at Collection content reader saw (#2099, #1870 R-31, R-33). */
export interface NoticeContentsEvidence {
  /** How the notice section was reached (R-34 context only; the Notice at Collection check reports it). */
  route: 'deep-link' | 'heading-search' | 'not-read';
  /** The section's heading, when one was located. */
  heading?: string;
  /** § 7012(e)(3): whether each category is sold or shared. */
  soldShared: NoticeContentsRow;
  /** § 7012(e)(5): the link to the Notice of Right to Opt-out of Sale/Sharing. */
  optOutLink: NoticeContentsRow;
}

/** Why the opt-out link destination pass reached its verdict (#2084). */
export type OptOutDestinationReason =
  | 'ok'
  | 'immediate-opt-out'
  | 'http-error'
  | 'loop'
  | 'no-load'
  | 'policy-top'
  | 'contact-only'
  | 'no-notice'
  | 'no-mechanism'
  | 'cookie-only'
  | 'pi-required'
  | 'account-required'
  | 'verification-required'
  | 'consent-required'
  | 'checkbox-required'
  | 'off-site-unapproved'
  | 'blocked-by-guard'
  | 'challenge'
  | 'js-no-effect'
  | 'no-limit-notice';

/** What the opt-out link destination pass saw (#2084). */
export interface OptOutDestinationEvidence {
  /** The opt-out link's URL, or the page holding a script control. */
  linkUrl: string;
  /** Where the visit landed after redirects. */
  finalUrl?: string;
  httpStatus?: number;
  verdict: 'pass' | 'fail' | 'unclear';
  reason: OptOutDestinationReason;
  /** The link was a script control the pass clicked, not a navigation. */
  immediate?: boolean;
  /**
   * Labels of the required personal-data fields, when the reason is `pi-required`
   * or `verification-required` (or `account-required` with a required field).
   */
  requiredFields?: string[];
  /**
   * Labels of the required checkboxes on the opt-out form (#2141), when the
   * reason is `consent-required` or `checkbox-required` (or one of the
   * personal-data reasons with a required checkbox beside it).
   */
  requiredCheckboxes?: string[];
}

/**
 * How the link opt-out pass left the opt-out (#2085): `confirmed` on-page
 * (a toggle reads off, or a confirmation showed), stopped before a field that
 * needs personal data, submitted to a back-end process with a stated
 * processing period, or not completed at all.
 */
export type LinkOptOutOutcome = 'confirmed' | 'needs_personal_data' | 'backend_pending' | 'not_completed';

/** One ad-tech beacon seen after the opt-out (#2085). Never holds a query string or an identifier value. */
export interface LinkOptOutBeacon {
  domain: string;
  path: string;
  /** The page load that sent it. */
  pageUrl: string;
  verdict: 'active' | 'restricted';
  /** Names of the identifiers it carried (`cookie`, `auid`, `vid`, `_fbp`, ...). */
  identifierKeys: string[];
  /** The restricted-processing signal, for a `restricted` beacon. */
  restrictedSignal?: string;
  /** The domain also fired after the consent simulation's Accept click. Absent when that pass did not run. */
  alsoInAccept?: boolean;
}

/** What the link opt-out pass saw (#2085). */
export interface LinkOptOutEvidence {
  outcome: LinkOptOutOutcome;
  verdict: 'pass' | 'fail' | 'unclear';
  /** One sentence for the report. */
  reason: string;
  /** The opt-out already read as on before the pass touched it. */
  alreadyOptedOut?: boolean;
  /** The later page loads watched (the homepage reload, then up to two same-site pages). */
  pagesVisited: string[];
  /** Identified, unrestricted ad beacons after a confirmed opt-out: the evidence for a fail. */
  persisting: LinkOptOutBeacon[];
  /** Beacons that carried a restricted-processing signal. */
  restricted: LinkOptOutBeacon[];
  /**
   * Clicks the pass made on the destination to complete the opt-out (#2159):
   * the toggle or opt-out button, plus a Save/Confirm control. The click on the
   * opt-out link itself and page loads are not counted. Absent on older reports
   * and when the pass stopped before reading the mechanism.
   */
  optOutSteps?: number;
  /** The opt-out path's step count against the banner's Accept (#2159). Absent when no banner Accept was pressed. */
  pathSymmetry?: OptOutPathSymmetry;
}

/** Whether the opt-out link path took more steps than the consent banner's Accept (#2159, § 7004(a)(2)). */
export interface OptOutPathSymmetry {
  verdict: 'pass' | 'fail' | 'unclear';
  reason: 'longer-than-opt-in' | 'no-longer-than-opt-in' | 'opt-out-incomplete' | 'opt-in-unknown';
  optOutSteps?: number;
  optInSteps?: number;
}

/** One ad-tech beacon seen in the GPC-off revisit (#2095). Never holds a query string or an identifier value. */
export interface GpcAbsenceBeacon {
  domain: string;
  path: string;
  /** The page load that sent it. */
  pageUrl: string;
  verdict: 'active' | 'restricted';
  /** Names of the identifiers it carried (`cookie:_fbp`, `auid`, `vid`, ...). */
  identifierKeys: string[];
  /** The restricted-processing signal, for a `restricted` beacon. */
  restrictedSignal?: string;
  /** The GPC-visit identifiers it reused, by name (`_fbp (cookie)`, `_uetvid (localStorage)`). */
  matchedIdentifiers?: string[];
}

/** What the GPC-absence pass saw (#2095): was the later absence of GPC read as consent? */
export interface GpcAbsenceEvidence {
  verdict: 'pass' | 'fail' | 'unclear' | 'not_applicable';
  /** One sentence for the report. */
  reason: string;
  /** The GPC-off loads watched (the homepage reload, then up to two same-site pages). */
  pagesVisited: string[];
  /** Persistent identifiers set during the GPC visit, by name only (`_fbp (cookie)`). Capped. */
  gpcVisitIdentifiers: string[];
  /** Unrestricted ad beacons that reused a GPC-visit identifier: the evidence for a fail. */
  reused: GpcAbsenceBeacon[];
  /** Beacons that carried a restricted-processing signal. */
  restricted: GpcAbsenceBeacon[];
  /** Unrestricted, identified ad beacons whose identifiers were all first set in the GPC-off visit. */
  newOnly: GpcAbsenceBeacon[];
}

// ---------------------------------------------------------------------------
// Report types — persistent, immutable snapshots of completed scans
// ---------------------------------------------------------------------------

/** Full report record as stored in the database */
export interface ReportRecord {
  id: string;
  scanId: string;
  name: string;
  domain: string;
  url: string;
  scannerVersion: string;
  scoringVersion: string;
  score: number;
  overallStatus: ComplianceStatus;
  scanData: ScanResult; // Full immutable snapshot
  createdAt: string;
  userId?: string;
  intentToken?: string;
  licenseStatus?: 'locked' | 'unlocked';
  /**
   * The scan lane that produced the report (`reports.kind`, #1760). Absent
   * on rows from before migration 032 whose lane is unknown.
   */
  kind?: ReportKind;
  notes?: string;
  deletedAt?: string;
  purgeAt?: string;
}

/**
 * The scan lane that produced a report (#1760). Only a `full` report can be
 * unlocked in place by `POST /api/reports/:id/unlock`.
 */
export type ReportKind = 'lite' | 'full' | 'policy';

/** Lightweight report summary for list views (omits full scan data) */
export interface ReportSummary {
  id: string;
  scanId: string;
  name: string;
  domain: string;
  url: string;
  scannerVersion: string;
  scoringVersion: string;
  score: number;
  overallStatus: ComplianceStatus;
  createdAt: string;
  userId?: string;
  /**
   * Primary email of the initiating Clerk user. Resolved server-side by the
   * admin branch of `GET /api/reports` only, so it is absent for non-admin
   * callers and whenever the Clerk lookup fails. Consumers must fall back to
   * `userId`.
   */
  userEmail?: string;
  intentToken?: string;
  licenseStatus?: 'locked' | 'unlocked';
  notes?: string;
  deletedAt?: string;
  purgeAt?: string;
}

/** Domain with report count for domain listing */
export interface DomainInfo {
  domain: string;
  reportCount: number;
  latestScore: number;
  latestReportDate: string;
}

// ---------------------------------------------------------------------------
// Sales report summary — redacted view for prospect sharing
// ---------------------------------------------------------------------------

/** Per-section summary for sales report (no detailed data) */
export interface SectionSummary {
  sectionKey: SectionName;
  displayName: string;
  status: ComplianceStatus;
  /**
   * percentage 0-100; null when no score was produced — either the section
   * never ran (skipped/error) or it ran and scored nothing (inconclusive GPC).
   */
  score: number | null;
  totalChecks: number;
  passedChecks: number;
  failedChecks: number;
  warningChecks: number;
  /**
   * How many of `failedChecks` errored rather than measured a failure.
   * Omitted when none did. An errored check measured nothing, so a surface
   * that states "failed N of M" subtracts this first.
   */
  errorChecks?: number;
  /** High-level stat lines, e.g. "15 third-party domains detected" */
  highlights: string[];
  /**
   * privacyPolicy only, and only when a policy was found: the disclosure
   * count its "N of M policy disclosures found" highlight states, as
   * numbers, so a surface can build its own sentence without parsing the
   * highlight's wording.
   */
  policyDisclosures?: { present: number; total: number };
}

/**
 * Self-reported context collected by the scan-time engagement wizard.
 * Values are whitelisted in models/engagement.ts (ENGAGEMENT_QUESTIONS).
 */
export interface SelfReportedContext {
  regulations?: string[];
  regions?: string;
  trafficBand?: string;
  companySizeBand?: string;
}

/** Sales-ready report summary — aggregate data only, no specifics */
export interface SalesReportSummary {
  reportId: string;
  domain: string;
  url: string;
  scanDate: string;
  scannerVersion: string;
  scoringVersion: string;
  /**
   * null when the scan earned no scoreable points at all (e.g. a GPC-only
   * lite scan that came back inconclusive, per #1128's follow-up review) —
   * a run that measured nothing is not the same as one that measured total
   * failure, and 0 renders as an F-grade "0%" total-failure claim in the
   * sales report and outreach pipeline, same reasoning as SectionSummary.score.
   */
  overallScore: number | null;
  overallStatus: ComplianceStatus;
  letterGrade: string | null;
  sections: SectionSummary[];
  /** Sum of penalties from all referenced enforcement actions */
  totalEnforcementExposure: string;
  /** Total number of distinct enforcement cases referenced */
  enforcementCaseCount: number;
  /** Self-reported context from the scan-time engagement wizard, when captured */
  selfReported?: SelfReportedContext;
  /**
   * Proof-of-scan captures the sales view renders as a thumbnail strip
   * (#1393). Metadata only; the JPEG is fetched from
   * `GET /api/reports/:id/screenshots/:key` with the share token. A locked
   * report lists its homepage capture only, the one that route serves it
   * (#1807). Absent on reports with no captures (created before #1337, or
   * with capture disabled).
   */
  screenshots?: SalesScreenshotRef[];
  /**
   * AI Crosscheck (policy judge) verdict counts over the CCPA checks (#1658),
   * so the shared view can introduce the crosscheck like the owner's report.
   * Aggregate counts only: no quote and no check name. Omitted when all three
   * are 0.
   */
  crosscheck?: CrosscheckCounts;
  /**
   * The scan lane behind the report (#1762), so the owner's locked view can
   * offer "Unlock this report" on a full audit and keep "Run full audit" on a
   * lite one. Omitted when the lane is unknown (rows from before #1760).
   */
  kind?: ReportKind;
  /** Whether the report is locked (#1807), so the teaser can say what the unlock adds. */
  locked?: boolean;
  /**
   * Whether the signed-in caller owns the report (#1807). The teaser offers
   * the owner "Unlock this report" and everyone else "Get your free audit",
   * because the unlock checkout is owner-only.
   */
  viewerIsOwner?: boolean;
  /**
   * The one-time unlock price in cents (`CREDIT_PACK_PRICE_CENTS`, the figure
   * `GET /api/credits` returns), so the non-owner fine print can name what the
   * findings cost without a signed-in credits call (#2133). Set by the summary
   * route only; omitted by an older backend, in which case the copy names no price.
   */
  packPrice?: number;
}

/**
 * AI Crosscheck verdicts a report displays (#1658). `confirmed`: the judge
 * agrees. `disputed`: an `upgraded`/`downgraded` verdict that was not applied.
 * `corrected`: any verdict with `applied: true` (enforce mode). Verdicts the
 * report never shows (`unverified`, `skipped`) are not counted.
 */
export interface CrosscheckCounts {
  confirmed: number;
  disputed: number;
  corrected: number;
  /**
   * The persisted `CheckAdjudication.mode`, never inferred from `corrected`:
   * `enforce` when any counted adjudication was produced in enforce mode (it
   * could have changed a status, even if none met the bar), else `advisory`
   * (`shadow` included; it never changes a status either).
   */
  mode: 'advisory' | 'enforce';
}

/**
 * The verdicts a report shows a reader (#1658). The single list the owner
 * report's per-card note, its AI Crosscheck banner and the shared summary's
 * counts all read, so a verdict added to `AdjudicationVerdict` stays hidden
 * everywhere until it is added here.
 */
export const CROSSCHECK_DISPLAYED_VERDICTS: ReadonlySet<AdjudicationVerdict> = new Set<AdjudicationVerdict>([
  'confirmed',
  'upgraded',
  'downgraded',
]);

/**
 * Counts the displayed AI Crosscheck verdicts over `checks` (#1658). The
 * caller picks the check set (CCPA sections plus non-offSite pages, deduped
 * by id); this only classifies.
 */
export function countCrosscheck(checks: ReadonlyArray<Pick<ComplianceCheck, 'adjudication'>>): CrosscheckCounts {
  const counts: CrosscheckCounts = { confirmed: 0, disputed: 0, corrected: 0, mode: 'advisory' };
  for (const { adjudication: a } of checks) {
    if (!a || !CROSSCHECK_DISPLAYED_VERDICTS.has(a.verdict)) continue;
    if (a.mode === 'enforce') counts.mode = 'enforce';
    if (a.applied) counts.corrected += 1;
    else if (a.verdict === 'confirmed') counts.confirmed += 1;
    else counts.disputed += 1;
  }
  return counts;
}

/** A `ScreenshotRef` flattened with the URL of the page it shows (#1393) */
export interface SalesScreenshotRef {
  key: string;
  role: PageRole;
  pageUrl: string;
  width: number;
  height: number;
  /**
   * The failing sections this capture shows the primary page of (#1474): the
   * homepage capture for a `non_compliant` UI finding, the first policy
   * capture for a `non_compliant` policy finding, each only when the finding's
   * `sourceUrls` name the page. Tracker and GPC findings are never paired: a
   * viewport capture cannot show network traffic. Section keys only, so the
   * summary still names no check. Omitted when the capture pairs with nothing.
   */
  evidenceFor?: SectionName[];
}

// ---------------------------------------------------------------------------
// Monitoring drift (#1319) — what changed between two consecutive monitored scans
// ---------------------------------------------------------------------------

/** One check whose status moved between the baseline and the current scan */
export interface DriftCheckTransition {
  /** Drift compares the CCPA sections only; CIPA exposure (#1392) is unscored */
  section: CcpaSectionName;
  /** The catalog check name — the stable identity across scans (`id` is minted per scan) */
  name: string;
  from: ComplianceStatus;
  to: ComplianceStatus;
  severity: Severity;
}

/**
 * The diff of a monitored scan against the previous monitored scan of the
 * same schedule (`diffReports`, backend scanner/drift.ts). Only sections that
 * ran on both sides are compared; the rest are listed in `excludedSections`
 * so a skipped or failed stage never reads as a change.
 */
export interface DriftSummary {
  /** Checks that got worse (compliant → warning → non_compliant) */
  regressions: DriftCheckTransition[];
  /** Checks that got better */
  improvements: DriftCheckTransition[];
  /**
   * Sale/share tracker domains (marketing/social the scanner scores as non-compliant)
   * present now and absent from the baseline. Other categories are not listed.
   */
  newTrackerDomains: string[];
  /**
   * Tracker domains the baseline saw blocked under GPC that now fire under GPC as a
   * sale/share; restricted-processing, consent-denial and CMP-managed hits are excluded.
   */
  trackersNowFiringUnderGpc: string[];
  score: number;
  previousScore: number;
  /** Sections that were skipped, errored, (GPC) inconclusive, (trackers) degraded or (policy) judged under enforce on one side only */
  excludedSections: CcpaSectionName[];
  /** True when the two scans ran different scanner or scoring versions, or a different policy judge under enforce, so a change may be ours, not the site's */
  versionChanged: boolean;
}

/** The most recent monitored run of a schedule, as `GET /api/monitors` returns it */
export interface MonitorRunSummary {
  reportId: string;
  previousReportId: string | null;
  /**
   * Regressed checks + new sale/share tracker domains + trackers newly firing under GPC.
   * 0 for a first run, and 0 when `drift.versionChanged` (the moves are listed, not counted).
   */
  regressionCount: number;
  /** Null for the first run of a schedule: there was no baseline to diff against. */
  drift: DriftSummary | null;
  createdAt: string;
}

/**
 * Machine-readable error codes (#1695). Every error body from the contract
 * routes (`POST /scans`, `POST /lite-scan`, `POST /anonymous-audit`,
 * `GET /scan-status/:id`, `POST /claim-scan`, `GET /reports/:id/summary`,
 * `POST /reports/:id/unlock`, `POST /billing/unlock-checkout`, `POST /shares`) carries one
 * as a top-level `code`, next to the unchanged prose `error`. Clients switch
 * on `code`, never on the prose. Additive by design: `error` stays a string,
 * so the 402 body keeps `error: 'insufficient_credits'` for existing readers.
 */
export const API_ERROR_CODES = Object.freeze([
  'INVALID_REQUEST',
  'INVALID_URL',
  'UNSAFE_URL',
  'SCAN_NOT_FOUND',
  'INVALID_INTENT_TOKEN',
  'SCAN_ALREADY_CLAIMED',
  'INSUFFICIENT_CREDITS',
  'QUEUE_AT_CAPACITY',
  'QUEUE_UNAVAILABLE',
  'ANON_AUDIT_DAILY_LIMIT',
  'REPORT_NOT_FOUND',
  'REPORT_DELETED',
  'REPORT_NOT_UNLOCKABLE',
  'REPORT_ACCESS_DENIED',
  'REPORT_ALREADY_UNLOCKED',
  'BILLING_NOT_CONFIGURED',
  'REPORT_EMAIL_LIMIT',
  'EMAIL_SEND_FAILED',
  'TOKEN_MISSING',
  'TOKEN_INVALID',
  'TOKEN_EXPIRED',
  'TOKEN_REVOKED',
  'TOKEN_MISMATCH',
  'UNAUTHENTICATED',
  'INTERNAL_ERROR',
] as const);

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/** One field-level problem in an `INVALID_REQUEST` body. */
export interface ApiRequestIssue {
  path: string;
  message: string;
}

/** Error body shape of the contract routes (#1695). Extra fields are allowed. */
export interface ApiErrorResponse {
  error: string;
  code: ApiErrorCode;
  message?: string;
  issues?: ApiRequestIssue[];
}

/** 202 body of `POST /api/lite-scan` and `POST /api/anonymous-audit` (#1777). */
export interface LiteScanStartResponse {
  scanId: string;
  intentToken: string;
  streamUrl: string;
  /**
   * Set only by `POST /api/anonymous-audit` past the global daily ceiling
   * (#1778): the request was accepted as today's GPC-only lite scan instead
   * of the locked full audit, so the client renders a lite result.
   */
  fallback?: 'gpc-only';
}

/**
 * 200 body of `POST /api/reports/:id/unlock` (#1760). `charged` is the number
 * of credits this call spent: 0 when the report was already unlocked or the
 * caller is unlimited or a monitoring subscriber.
 */
export interface UnlockReportResponse {
  unlocked: true;
  charged: 0 | 1;
}

/**
 * Body of `POST /api/scans/:scanId/email-report` (#1780). The intent token is
 * the write credential; `consent` must be `true`. Accepted while the scan is
 * still running too (see EmailReportResponse.scheduled).
 */
export interface EmailReportRequest {
  intentToken: string;
  email: string;
  consent: true;
  /** Meta browser ids (`_fbp`/`_fbc`) for the Contact conversion; absent when the visitor opted out. */
  meta?: { fbp?: string; fbc?: string };
  /**
   * True when the browser has opted out (GPC or a declined banner). The call
   * is cross-origin and carries no cookies, so the server cannot read the
   * banner choice itself; this stops the Contact conversion.
   */
  optOut?: boolean;
}

/**
 * 200 body of `POST /api/scans/:scanId/email-report`. `sent` is false when
 * mail is not configured. `scheduled` is true when the scan had no report yet
 * (still queued or running): the address is kept and the email goes out when
 * the scan finishes, so `sent` is false then too.
 */
export interface EmailReportResponse {
  sent: boolean;
  scheduled?: boolean;
}

/**
 * How far a report's reader got through the Privisy Shield offer, a fake-door
 * test for a product that is not built yet: the offer rendered (`shown`), the
 * reader opened it (`clicked`), or left an address to hear at launch
 * (`reserved`).
 */
export type ShieldInterestEvent = 'shown' | 'clicked' | 'reserved';

/** Body of `POST /api/reports/:id/shield-interest`. `email` is required for `reserved` and refused otherwise. */
export interface ShieldInterestRequest {
  event: ShieldInterestEvent;
  email?: string;
}

/**
 * 200 body of `POST /api/reports/:id/shield-interest`. `recorded` is false
 * when the event was already recorded for the report, or the report already
 * holds the most reservations it may.
 */
export interface ShieldInterestResponse {
  recorded: boolean;
}

/**
 * Which paywall control a reader pressed on a locked report:
 * `unlock_credit` spends a credit, `unlock_purchase` opens the one-off audit
 * checkout, `monitor` opens the monitoring subscription checkout.
 */
export type PaywallOffer = 'unlock_credit' | 'unlock_purchase' | 'monitor';

/** Body of `POST /api/reports/:id/paywall-event`. Only clicks come from the browser. */
export interface PaywallEventRequest {
  event: 'cta_clicked';
  offer: PaywallOffer;
}

/** 200 body of `POST /api/reports/:id/paywall-event`. */
export interface PaywallEventResponse {
  recorded: boolean;
}

/** 200 body of `POST /api/claim-scan`. */
export interface ClaimScanResponse {
  message: string;
  scanId: string;
  reportId: string | null;
}

// ---------------------------------------------------------------------------
// Account, billing, queue and share-link payloads (moved from the dashboard,
// tech-debt Phase 3) so the backend routes can check their responses against
// the same declarations.
// ---------------------------------------------------------------------------

/**
 * What the account is entitled to (backend middleware/freemium.ts, #1316).
 * `free`/`scan` spend credits; `monitor`/`agency` are subscriptions, and still
 * spend a credit on an ad-hoc scan. Unlimited is NOT a tier: read it from
 * `balance === null` (/api/credits) or `unlimited` (admin payloads).
 * Admins and PRIVISY_PRO_USER_IDS accounts also report `agency` (with
 * unlimited), so `agency` alone does not mean a paying Agency customer.
 */
export type Tier = 'free' | 'scan' | 'monitor' | 'agency';

/**
 * GET /api/billing/subscription (#1315). `status: 'none'` (with a null plan)
 * means the caller has no subscription Stripe can still charge; otherwise
 * `status` is Stripe's own string. `entitled` is false for a past_due /
 * unpaid / incomplete subscription that no longer grants the plan: its owner
 * must fix payment in the portal, not start a second subscription. Dates are
 * ISO strings.
 */
export interface BillingSubscriptionResponse {
  plan: 'monitor' | 'unknown' | null;
  status: string;
  currentPeriodEnd: string | null;
  cancelAt: string | null;
  entitled: boolean;
}

/** POST /api/billing/checkout-session and /api/billing/portal-session (#1315). */
export interface BillingSessionResponse {
  url: string;
}

/**
 * One monitored domain (#1318). Scanned weekly by the cron-driven
 * POST /api/monitors/run-due; `lastReportId` is null until the first
 * scheduled scan settles (or after its report is purged). Dates are ISO strings.
 */
export interface MonitorSchedule {
  id: string;
  userId: string;
  domain: string;
  startUrl: string;
  maxPages: number;
  cadence: 'weekly';
  nextRunAt: string;
  lastRunAt: string | null;
  lastScanId: string | null;
  lastReportId: string | null;
  /** The newest recorded run and what changed since the one before it (#1319); null before the first run. */
  lastRun: MonitorRunSummary | null;
  enabled: boolean;
  createdAt: string;
}

/** GET /api/monitors (#1318). `limit` is how many domains the caller's tier may monitor. */
export interface MonitorListResponse {
  monitors: MonitorSchedule[];
  limit: number;
}

export type ShareLinkStatus = 'active' | 'expired' | 'revoked';

export interface ShareLinkRecord {
  id: string;
  reportId: string;
  token: string;
  label?: string | null;
  createdAt: string;
  expiresAt: string;
  revokedAt?: string | null;
  status: ShareLinkStatus;
  shareLink?: string;
}

export interface CreditTransaction {
  id: string;
  amount: number;
  type: 'purchase' | 'free_grant' | 'scan_deduct' | 'refund';
  reference_id: string | null;
  created_at: string;
  createdAt?: string;
  domain: string | null;
  report_id: string | null;
}

export interface QueuedScanEntry {
  id: string;
  url: string;
  queuedAt: string;
  /** The scan's owner; absent for an anonymous scan. */
  userId?: string;
}

export interface ActiveScanEntry {
  id: string;
  url: string;
  startedAt: string;
  /** The scan's owner; absent for an anonymous scan. */
  userId?: string;
}

export interface QueueStatus {
  active: ActiveScanEntry[];
  pending: QueuedScanEntry[];
  maxConcurrent: number;
}

export interface PurchaseStatusResponse {
  status: 'confirmed' | 'pending';
  /** @deprecated credit-based purchases no longer return a scanId */
  scanId?: string;
  creditsAdded?: number;
  /** What Stripe actually settled the session for, in cents. Undefined when the ledger row predates this field or the checkout session carried no amount_total. */
  amountCents?: number;
}

export interface CreditInfoResponse {
  balance: number | null; // null = unlimited (PRIVISY_PRO_USER_IDS or admin)
  tier: Tier;
  packPrice: number;
  creditsPerPurchase: number;
  /**
   * False when every purchase was an "unlock this report" checkout (#1776).
   * Optional so an older backend reads as true.
   */
  hasCreditHistory?: boolean;
}

export interface CreditHistoryResponse {
  transactions: CreditTransaction[];
  total: number;
}
